import { useCallback, useMemo } from 'react';
import type { TimeSeriesResponse } from 'api';
import { useStore } from 'common/store';
import { ChartSeries } from 'components/Charts';
import { useGetCompanyMetric } from 'services/hooks/companies';
import { UseChartDataParams, UseChartDataReturn } from '../types';

const MOCK_MOIC_DATA: TimeSeriesResponse = {
  company_id: 9999,
  current: {
    date: '2024-12-31',
    value: '4',
  },
  change_percent: '-12.12',
  history: [
    { date: '2023-12-31', value: '6.2' },
    { date: '2024-03-31', value: '6.5' },
    { date: '2024-06-30', value: '6.1' },
    { date: '2024-09-30', value: '5.9' },
    { date: '2024-12-31', value: '5.8' },
  ],
};

const USE_MOCK_DATA = false;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const useMoic = ({ selectedMeasurementDate, units }: UseChartDataParams): UseChartDataReturn => {
  const [{ companyInfo }] = useStore();

  // Fetch moic metric data
  const {
    data: apiData,
    isLoading: isLoadingMoic,
    isError: isErrorMoic,
  } = useGetCompanyMetric<TimeSeriesResponse>({
    company: companyInfo,
    metricName: 'moic',
    measurementDate: selectedMeasurementDate,
    shouldQueryAutomatically: !USE_MOCK_DATA, // Disable API call when using mock data
  });

  // Use mock data if flag is enabled, otherwise use API data
  const moicData = USE_MOCK_DATA ? MOCK_MOIC_DATA : apiData;

  // Helper to convert hex to RGB
  const hexToRgb = useCallback((hex: string): { r: number; g: number; b: number } | null => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
      : null;
  }, []);

  // Helper to darken a color
  const darkenColor = useCallback((color: string, percent: number): string => {
    const rgb = color.match(/\d+/g);
    if (!rgb || rgb.length !== 3) {
      return color;
    }

    const r = Math.max(0, parseInt(rgb[0], 10) - percent);
    const g = Math.max(0, parseInt(rgb[1], 10) - percent);
    const b = Math.max(0, parseInt(rgb[2], 10) - percent);

    return `rgb(${r}, ${g}, ${b})`;
  }, []);

  // Helper to interpolate between two hex colors
  const interpolateColor = useCallback(
    (color1: string, color2: string, factor: number): string => {
      const c1 = hexToRgb(color1);
      const c2 = hexToRgb(color2);
      if (!c1 || !c2) {
        return color1;
      }

      const r = Math.round(c1.r + (c2.r - c1.r) * factor);
      const g = Math.round(c1.g + (c2.g - c1.g) * factor);
      const b = Math.round(c1.b + (c2.b - c1.b) * factor);

      return `rgb(${r}, ${g}, ${b})`;
    },
    [hexToRgb]
  );

  // Helper function to get color based on MOIC value
  // Higher values = warmer colors (green/gold), lower values = cooler colors (blue/purple)
  const getColorForValue = useCallback(
    (value: number, min: number, max: number): { base: string; darker: string } => {
      if (max === min) {
        // Default purple gradient if all values are the same
        return { base: '#9b59b6', darker: '#8e44ad' };
      }

      // Normalize value to 0-1 range
      const normalized = (value - min) / (max - min);

      // Color stops for the gradient (cool to warm)
      const colorStops = [
        { pos: 0, color: '#5DADE2' }, // Blue (lowest)
        { pos: 0.25, color: '#9b59b6' }, // Purple
        { pos: 0.5, color: '#E74C3C' }, // Red
        { pos: 0.75, color: '#F39C12' }, // Orange
        { pos: 1, color: '#27AE60' }, // Green (highest)
      ];

      // Find the two color stops to interpolate between
      let lowerStop = colorStops[0];
      let upperStop = colorStops[colorStops.length - 1];

      for (let i = 0; i < colorStops.length - 1; i++) {
        if (normalized >= colorStops[i].pos && normalized <= colorStops[i + 1].pos) {
          lowerStop = colorStops[i];
          upperStop = colorStops[i + 1];
          break;
        }
      }

      // Interpolate between the two colors
      const t = (normalized - lowerStop.pos) / (upperStop.pos - lowerStop.pos || 1);
      const baseColor = interpolateColor(lowerStop.color, upperStop.color, t);
      const darkerColor = darkenColor(baseColor, 15);

      return { base: baseColor, darker: darkerColor };
    },
    [interpolateColor, darkenColor]
  );

  // Transform API data to ChartSeries format
  const lineChartSeries: ChartSeries[] = useMemo(() => {
    if (!moicData?.history) {
      return [];
    }

    const dataPoints = moicData.history.map(item => ({
      x: item.date,
      y: Number(item.value),
    }));

    // Calculate min and max for color scaling
    const values = dataPoints.map(p => p.y);
    const min = Math.min(...values);
    const max = Math.max(...values);

    // Store color info in metadata for use in chart options
    const dataWithColors = dataPoints.map(point => ({
      ...point,
      metadata: {
        colors: getColorForValue(point.y, min, max),
      },
    }));

    return [
      {
        id: 'moic',
        label: 'MOIC',
        data: dataWithColors,
        color: '#9b59b6', // Fallback color
        config: {
          borderRadius: [8, 8, 0, 0],
          shadowBlur: 10,
          shadowOffsetY: 4,
          emphasis: {
            itemStyle: {
              shadowBlur: 15,
              shadowOffsetY: 6,
            },
          },
          barWidth: '60%',
        },
      },
    ];
  }, [moicData, getColorForValue]);

  // Get current value and percentage change for Indicator
  const indicatorValue = useMemo(() => {
    if (!moicData) {
      return { value: '0', percentage: 0 };
    }

    return {
      value: `${moicData.current.value}x`,
      percentage: Number(moicData.change_percent),
    };
  }, [moicData]);

  return {
    lineChartSeries,
    indicatorValue,
    isLoading: USE_MOCK_DATA ? false : isLoadingMoic,
    isError: USE_MOCK_DATA ? false : isErrorMoic,
  };
};
